<template>
    <div>
        <vue-html2pdf
            v-if="isShown"
            :show-layout="true"
            :float-layout="true"
            :enable-download="true"
            :preview-modal="true"
            :paginate-elements-by-height="1400"
            filename="analytics-export"
            :pdf-quality="2"
            :manual-pagination="true"
            pdf-format="a4"
            pdf-orientation="portrait"
            pdf-content-width="800px"
            ref="html2Pdf"
            :html-to-pdf-options="{
                filename: 'analytics-export',
                html2canvas: {
                    scrollX: 0,
                    scrollY: 0,
                },
            }"
        >
            <section slot="pdf-content">
                <section class="pdf-item export-container">
                    <div class="row mb-2">
                        <div class="col-2">
                            <img
                                src="/images/ionaute_logo_blue.svg"
                                class="pull-left"
                                alt="Ionaute"
                                height="20"
                            />
                        </div>
                        <div class="col-8"></div>
                        <div class="col-2">
                            <span class="date pull-right">{{
                                date.toLocaleString("en-GB", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                })
                            }}</span>
                        </div>
                    </div>

                    <h4 class="text-center mb-3">
                        Performance analytics
                        <span class="company">{{ companyTitle }}</span> for
                        <span class="range-name">{{ rangeName }}</span>
                    </h4>

                    <div class="row">
                        <div
                            v-for="chart in filteredCharts"
                            :key="chart.key"
                            :class="{
                                'col-6': chart.weight === 0.5,
                                'col-12': chart.weight === 1,
                            }"
                        >
                            <template v-if="chart.key === 1">
                                <tally :is-export="true"></tally>
                            </template>

                            <template v-else-if="chart.key === 2">
                                <analytics-matrix
                                    :range="rangeName"
                                    :is-export="true"
                                ></analytics-matrix>
                            </template>

                            <template v-else-if="chart.key === 3">
                                <pass-rate-container
                                    :range="rangeName"
                                    :is-export="true"
                                ></pass-rate-container>
                            </template>

                            <template v-else-if="chart.key === 4">
                                <h4 class="mt-3">Total Exports</h4>
                                <b-card class="card-chart">
                                    <monthly-exports-container
                                        :range="rangeName"
                                        :is-export="true"
                                    ></monthly-exports-container>
                                </b-card>
                            </template>

                            <template v-else-if="chart.key === 5">
                                <h4 class="mt-3">Fail Category Spread</h4>
                                <b-card class="card-chart">
                                    <fail-category-container
                                        :is-export="true"
                                    ></fail-category-container>
                                </b-card>
                            </template>
                        </div>
                    </div>
                </section>
            </section>
        </vue-html2pdf>

        <button class="btn btn-primary export-btn" @click="toggleExport">
            {{ $t("Export to PDF") }}
        </button>

        <div class="control-panel" v-if="isShown">
            <h3 class="text-center mt-3">Export settings</h3>
            <div class="form-check mt-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isShowTally"
                    @change="changeChartState(1, isShowTally)"
                    id="tally"
                />
                <label class="form-check-label" for="tally"> Show Tally </label>
            </div>
            <div class="form-check mt-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isShowOverallStatistics"
                    @change="changeChartState(2, isShowOverallStatistics)"
                    id="overall"
                />
                <label class="form-check-label" for="overall">
                    Show Overall Statistics
                </label>
            </div>
            <div class="form-check mt-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isShowPassRates"
                    @change="changeChartState(3, isShowPassRates)"
                    id="pass-rates"
                />
                <label class="form-check-label" for="pass-rates">
                    Show Pass Rates
                </label>
            </div>
            <div class="form-check mt-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isShowTotalExports"
                    @change="changeChartState(4, isShowTotalExports)"
                    id="total"
                />
                <label class="form-check-label" for="total">
                    Show Total Exports
                </label>
            </div>
            <div class="form-check mt-3">
                <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="isShowFailCategorySpread"
                    @change="changeChartState(5, isShowFailCategorySpread)"
                    id="category-spread"
                />
                <label class="form-check-label" for="category-spread">
                    Show Fail Category Spread
                </label>
            </div>
            <button
                class="btn btn-outline-secondary mt-3"
                @click="isShown = false"
            >
                Hide export form
            </button>
            <button
                class="btn btn-outline-secondary mt-3 mb-3"
                @click="generateReport"
            >
                Generate PDF
            </button>
        </div>
    </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import MonthlyExportsContainer from "../MonthlyExportsContainer";
import PassRateContainer from "../PassRateContainer";
import FailCategoryContainer from "../FailCategoryContainer";
import Tally from "../Tally";

export default {
  name: "ExportComponent",
  components: {
    VueHtml2pdf,
    FailCategoryContainer,
    PassRateContainer,
    MonthlyExportsContainer,
    Tally
  },
  props: {
    rangeName: String,
    companyTitle: String
  },
  data() {
    return {
      isHasSecondPage: true,
      isShowTally: true,
      isShowOverallStatistics: true,
      isShowPassRates: true,
      isShowTotalExports: true,
      isShowFailCategorySpread: true,
      isShown: false,
      date: new Date(),
      charts: [
        {
          key: 1,
          type: "tally",
          enabled: true,
          weight: 1,
          page: 1
        },
        {
          key: 2,
          type: "overall statistics",
          enabled: true,
          weight: 1,
          page: 1
        },
        {
          key: 3,
          type: "pass rates",
          enabled: true,
          weight: 0.5,
          page: 1
        },
        {
          key: 4,
          type: "total exports",
          enabled: true,
          weight: 0.5,
          page: 1
        },
        {
          key: 5,
          type: "fail category spread",
          enabled: true,
          weight: 0.5,
          page: 1
        }
      ]
    };
  },
  computed: {
    filteredCharts() {
      return this.charts.filter(chart => chart.enabled);
    }
  },
  methods: {
    showModal() {
      this.$bvModal.show("modal-export");
    },
    toggleExport() {
      this.isShown = !this.isShown;
    },
    generateReport() {
      this.$refs.html2Pdf.generatePdf();
    },
    /**
     * Paginating charts
     *
     * @param chartType
     * @param state
     */
    changeChartState(chartType, state) {
      let weight = 0;
      let page = 1;

      this.isHasSecondPage = false;

      this.charts.forEach(
        function(chart) {
          if (chart.key === chartType) {
            chart.enabled = state;
          }

          if (chart.enabled) {
            weight += chart.weight;

            if (weight > 4) {
              page++;
              weight = 0;
            }

            chart.page = page;

            if (page >= 2) {
              this.isHasSecondPage = true;
            }
          }
        }.bind(this)
      );
    }
  }
};
</script>

<style scoped>
.export-btn {
  width: 100%;
}

.export-container {
  background: white;
  padding: 30px;
}

.control-panel {
  position: fixed;
  z-index: 999999;
  padding: 15px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
  background: #fff;
  border-radius: 5px;
  right: 25px;
  top: 50px;
  width: 405px;
  box-shadow: 5px 5px 10px #000;
  max-height: calc(100vh - 50px);
  -webkit-animation-name: animate-fade-in-top-data-v-8ce406ca;
  animation-name: animate-fade-in-top-data-v-8ce406ca;
  -webkit-animation-duration: 0.3s;
  animation-duration: 0.3s;
  -webkit-animation-delay: 0.2s;
  animation-delay: 0.2s;
  -webkit-animation-fill-mode: forwards;
  animation-fill-mode: forwards;
  -webkit-animation-timing-function: ease-in-out;
  animation-timing-function: ease-in-out;
}

.company {
  font-style: italic;
}

.card {
  box-shadow: none !important;
}
</style>
